/**
 * Data layer for notifications and the delivery policy (ARCHITECTURE.md §7,
 * §8; ARCHITECTURE-LAYERS.md §4.1): `hc<AppType>` fetchers and `queryOptions`
 * factories. Mutation hooks live in `./hooks`.
 */
import { queryOptions } from "@tanstack/react-query";
import type { BadgeTone } from "@richrich/ui";
import type {
  ListNotificationsResponse,
  NotificationEventType,
  NotificationPolicy,
  MarkNotificationsRead,
  MarkNotificationsReadResponse,
} from "@richrich/contracts";
import { api, unwrap } from "../../lib/api";
import { keys } from "../../lib/queryKeys";

/** Recent notifications feed (`GET /api/notifications`). */
export const notificationsQuery = queryOptions({
  queryKey: keys.notifications.list(),
  queryFn: async (): Promise<ListNotificationsResponse> =>
    unwrap<ListNotificationsResponse>(await api.notifications.$get({ query: { limit: "100" } })),
  staleTime: 15_000,
});

/** Delivery policy (`GET /api/notifications/policy`). */
export const notificationPolicyQuery = queryOptions({
  queryKey: keys.notifications.policy(),
  queryFn: async (): Promise<NotificationPolicy> =>
    unwrap<NotificationPolicy>(await api.notifications.policy.$get()),
  staleTime: 60_000,
});

/** `POST /api/notifications/read` — internal `mutationFn` for {@link useMarkNotificationsRead} (`./hooks`). */
export async function markNotificationsRead(
  body: MarkNotificationsRead,
): Promise<MarkNotificationsReadResponse> {
  return unwrap<MarkNotificationsReadResponse>(await api.notifications.read.$post({ json: body }));
}

/** `PUT /api/notifications/policy` — internal `mutationFn` for {@link useUpdateNotificationPolicy} (`./hooks`). */
export async function updateNotificationPolicy(
  policy: NotificationPolicy,
): Promise<NotificationPolicy> {
  return unwrap<NotificationPolicy>(await api.notifications.policy.$put({ json: policy }));
}

/** Presentation metadata per notification event type (§2 event taxonomy). */
export const EVENT_META: Record<NotificationEventType, { label: string; tone: BadgeTone }> = {
  signal: { label: "Signal", tone: "info" },
  orderPlaced: { label: "Order placed", tone: "info" },
  orderFilled: { label: "Order filled", tone: "good" },
  approvalRequested: { label: "Approval requested", tone: "warning" },
  aiVerdict: { label: "AI verdict", tone: "neutral" },
  riskBreach: { label: "Risk breach", tone: "serious" },
  error: { label: "Error", tone: "critical" },
};

/** Stable display order for the policy matrix rows. */
export const EVENT_TYPES: readonly NotificationEventType[] = [
  "signal",
  "orderPlaced",
  "orderFilled",
  "approvalRequested",
  "aiVerdict",
  "riskBreach",
  "error",
];
